<template>
  <div class="p-6 md:p-8 bg-[#F1F5F9] min-h-screen font-sans text-slate-800 select-none">
    
    <!-- HEADER / MODE SELECTOR -->
    <header class="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black shadow-md shadow-blue-500/20">
          POS
        </div>
        <div>
          <h1 class="text-lg font-black text-slate-900 uppercase tracking-tight">
            {{ t.adminPanel }}
          </h1>
          <p class="text-xs text-slate-500 font-semibold">{{ t.admin }} {{ userData.firstName }} {{ userData.lastName }}</p>
        </div>
      </div>

      <!-- Mode Toggle Switch -->
      <div class="flex gap-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200/60 w-full lg:w-auto">
        <button
          @click="currentMode = 'admin'"
          :class="currentMode === 'admin' ? 'bg-white text-blue-600 shadow-sm font-black' : 'text-slate-600 font-semibold hover:text-slate-900'"
          class="flex-1 lg:flex-none px-5 py-2 rounded-lg text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {{ t.adminMode }}
        </button>

        <button
          @click="currentMode = 'counter'"
          :class="currentMode === 'counter' ? 'bg-white text-blue-600 shadow-sm font-black' : 'text-slate-600 font-semibold hover:text-slate-900'"
          class="flex-1 lg:flex-none px-5 py-2 rounded-lg text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {{ t.counterMode }}
        </button>
      </div>
    </header>

    <!-- ================= MODE ADMIN ================= -->
    <div v-if="currentMode === 'admin'" class="grid xl:grid-cols-3 gap-6">
      
      <!-- Colonne principale (2/3) : KPIs & Liste des tickets -->
      <div class="xl:col-span-2 space-y-6">
        
        <!-- Stats Cards POS Style -->
        <div class="grid md:grid-cols-3 gap-4">
          <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
            <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{{ t.waiting }}</p>
            <p class="text-3xl font-black text-amber-500">{{ waitingTickets.length }}</p>
          </div>
          <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
            <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{{ t.called }}</p>
            <p class="text-3xl font-black text-blue-600">{{ calledTickets.length }}</p>
          </div>
          <div class="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm">
            <p class="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1">{{ t.completed }}</p>
            <p class="text-3xl font-black text-emerald-600">{{ completedTickets.length }}</p>
          </div>
        </div>

        <!-- Tickets List Container -->
        <div class="bg-white rounded-2xl shadow-sm p-6 border border-slate-200/80">
          <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
            <h2 class="text-base font-black uppercase tracking-wide text-slate-900">{{ t.allTickets }}</h2>

            <!-- Filter Tabs -->
            <div class="flex gap-1.5 flex-wrap">
              <button
                v-for="status in ['ALL', 'WAITING', 'CALLED', 'DONE']"
                :key="status"
                @click="filterStatus = status"
                :class="filterStatus === status ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
                class="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase transition-all"
              >
                {{ t[status.toLowerCase()] }}
              </button>
            </div>
          </div>

          <!-- Tickets Table / List -->
          <div class="space-y-2.5 max-h-[550px] overflow-y-auto pr-1">
            <div
              v-for="ticket in filteredTickets"
              :key="ticket._id"
              @click="selectTicketForCall(ticket)"
              class="p-4 rounded-xl border border-slate-200/70 bg-slate-50/50 hover:bg-white hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group flex justify-between items-center"
            >
              <div class="flex items-center gap-4">
                <span class="text-2xl font-black text-slate-800 w-20">{{ ticket.queueId?.prefix }}{{ ticket.number }}</span>
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span :class="getStatusBadgeClass(ticket.status)" class="px-2.5 py-0.5 rounded-md text-[9px] font-black uppercase">
                      {{ t[ticket.status.toLowerCase()] }}
                    </span>
                    <span class="text-xs font-bold text-slate-600">{{ ticket.queueId?.name || t.general }}</span>
                  </div>
                  <p class="text-[11px] text-slate-400 font-medium">{{ t.created }}: {{ formatTime(ticket.createdAt) }} — {{ t.waitTime }}: {{ getWaitTime(ticket.createdAt) }} min</p>
                </div>
              </div>

              <div class="flex items-center gap-3">
                <div v-if="ticket.counterId" class="text-right hidden sm:block">
                  <p class="text-xs font-bold text-blue-600">
                    {{ getCounterName(ticket.counterId) }}
                  </p>
                </div>
                <button
                  @click.stop="selectTicketForCall(ticket)"
                  class="bg-blue-600 text-white px-3.5 py-2 rounded-xl text-xs font-black uppercase hover:bg-blue-700 transition-all shadow-sm"
                >
                  {{ t.assignToCounter }}
                </button>
              </div>
            </div>

            <div v-if="filteredTickets.length === 0" class="text-center py-12 text-slate-400 font-bold uppercase text-xs">
              {{ t.noTickets }}
            </div>
          </div>
        </div>
      </div>

      <!-- Colonne latérale (1/3) : Historique -->
      <div class="space-y-6">
        <div class="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
          <div class="flex items-center justify-between mb-5">
            <h3 class="text-xs font-black uppercase tracking-widest text-slate-400">{{ t.history }}</h3>
            <span class="bg-emerald-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md">{{ completedTickets.length }}</span>
          </div>

          <div class="space-y-2.5 max-h-[620px] overflow-y-auto pr-1">
            <div
              v-for="ticket in completedTickets"
              :key="ticket._id"
              class="p-3.5 rounded-xl border border-slate-200/60 bg-slate-50/50 hover:bg-white transition-all group"
            >
              <div class="flex justify-between items-start mb-2">
                <div>
                  <p class="text-base font-black text-slate-800">{{ ticket.queueId?.prefix }}{{ ticket.number }}</p>
                  <p class="text-[9px] font-bold text-slate-400 uppercase">{{ ticket.queueId?.name }}</p>
                </div>
                <span class="bg-emerald-50 text-emerald-600 border border-emerald-200/60 px-2 py-0.5 rounded-md text-[9px] font-black">
                  {{ t.done }}
                </span>
              </div>

              <div class="flex justify-between items-center mt-3 pt-2 border-t border-slate-100">
                <span class="text-[10px] text-slate-400 font-medium">{{ formatTime(ticket.updatedAt) }}</span>
                <button
                  @click="recallTicketFromHistory(ticket)"
                  class="bg-amber-50 text-amber-600 border border-amber-200/60 px-3 py-1 rounded-lg text-[10px] font-black uppercase hover:bg-amber-100 transition-all"
                >
                  {{ t.recall }}
                </button>
              </div>
            </div>

            <div v-if="completedTickets.length === 0" class="text-center py-12 text-slate-400 font-bold uppercase text-xs">
              {{ t.noHistory }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= MODE COUNTER (CAISSE / GUICHET) ================= -->
    <div v-if="currentMode === 'counter'">
      
      <!-- Étape 1 : Sélection du Guichet -->
      <div v-if="!selectedCounterId" class="max-w-3xl mx-auto mt-10">
        <div class="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-sm text-center">
          <div class="w-14 h-14 bg-blue-50 border border-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
          </div>
          <h2 class="text-xl font-black text-slate-900 mb-2 uppercase">{{ t.selectCounter }}</h2>
          <p class="text-xs text-slate-500 mb-6 font-medium">Veuillez choisir le poste ou la caisse active pour commencer l'appel des tickets.</p>
          
          <div class="grid md:grid-cols-2 gap-3 text-left">
            <button
              v-for="counter in availableCounters"
              :key="counter._id"
              @click="selectCounter(counter._id)"
              class="p-4 border border-slate-200 rounded-xl hover:border-blue-600 hover:bg-blue-50/40 transition-all group bg-slate-50/50"
            >
              <p class="text-sm font-black text-slate-900 group-hover:text-blue-600 mb-1">{{ counter.name }}</p>
              <p class="text-xs text-slate-500 font-medium truncate">{{ counter.serviceIds?.map(s => s.name).join(', ') || 'Général' }}</p>
            </button>
          </div>
        </div>
      </div>

      <!-- Étape 2 : Interface Poste / Caisse active style POS -->
      <div v-else>
        <div class="flex justify-between items-center mb-6 bg-white px-6 py-4 rounded-2xl border border-slate-200/80 shadow-sm">
          <div class="flex items-center gap-3">
            <span class="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></span>
            <h2 class="text-sm font-black uppercase tracking-wider text-slate-900">{{ t.station }}: <span class="text-blue-600">{{ getCounterName(selectedCounterId) }}</span></h2>
          </div>
          <button
            @click="selectedCounterId = null"
            class="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
          >
            {{ t.changeCounter }}
          </button>
        </div>

        <div class="grid xl:grid-cols-3 gap-6">
          
          <!-- Écran principal du guichet -->
          <div class="xl:col-span-2 space-y-6">
            
            <div v-if="waitingTicketsForCounter.length === 0 && !currentTicket"
                 class="bg-white border border-slate-200/80 rounded-2xl p-10 flex flex-col items-center text-center shadow-sm">
              <div class="w-14 h-14 bg-emerald-50 border border-emerald-100 rounded-2xl flex items-center justify-center text-emerald-600 mb-3">
                <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 class="text-lg font-black text-slate-900 uppercase">{{ t.emptyQueue }}</h2>
              <p class="text-slate-500 text-xs font-medium mt-1">{{ t.allClientsServed }}</p>
            </div>

            <div v-else class="bg-white rounded-2xl shadow-sm p-6 border border-slate-200/80">
              <div class="flex justify-between items-center mb-6 pb-4 border-b border-slate-100">
                <div>
                  <span class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ t.inProgress }}</span>
                  <h2 class="text-sm font-extrabold text-slate-800">{{ currentTicket?.queueId?.name || t.service }}</h2>
                </div>
                <span class="px-3 py-1 bg-slate-900 text-white rounded-lg text-xs font-black uppercase tracking-wider">
                  {{ t.ticket }} #{{ currentTicket?._id ? currentTicket._id.slice(-4) : '0000' }}
                </span>
              </div>

              <!-- Grand affichage numéro de caisse / POS -->
              <div class="flex flex-col items-center justify-center py-12 bg-[#0B0F19] rounded-2xl mb-6 shadow-inner text-white">
                <p class="text-[10px] font-black text-blue-400 uppercase tracking-[0.3em] mb-2">{{ t.callNumber }}</p>
                <div class="text-8xl font-black tracking-tighter tabular-nums text-white">
                  {{ currentTicket?.number || '---' }}
                </div>
                <div class="mt-4">
                  <span class="px-3 py-1 bg-white/10 rounded-lg text-[10px] font-bold text-slate-300 uppercase tracking-widest">
                    {{ currentTicket?.status === 'CALLED' ? t.called : t.waiting }}
                  </span>
                </div>
              </div>

              <!-- Boutons d'actions rapides style POS -->
              <div class="grid grid-cols-3 gap-3 mb-4">
                <button @click="callAgainCounter" class="flex flex-col items-center p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200/80 transition-all group">
                  <svg class="w-5 h-5 mb-1.5 text-slate-400 group-hover:text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5 9v6h4l5 5V4L9 9H5z" /></svg>
                  <span class="text-[10px] font-black uppercase tracking-wider text-slate-700">{{ t.recall }}</span>
                </button>

                <button @click="processNextCounter" :disabled="loading" class="flex flex-col items-center p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md shadow-blue-500/20 transition-all disabled:opacity-50">
                  <svg v-if="!loading" class="w-6 h-6 mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                  <span v-else class="animate-spin rounded-full h-6 w-6 border-2 border-white mb-1.5"></span>
                  <span class="text-xs font-black uppercase tracking-wider">{{ t.next }}</span>
                </button>

                <button @click="markAbsentCounter" class="flex flex-col items-center p-4 bg-slate-50 hover:bg-rose-50 rounded-xl border border-slate-200/80 group transition-all">
                  <svg class="w-5 h-5 mb-1.5 text-slate-400 group-hover:text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  <span class="text-[10px] font-black uppercase tracking-wider text-slate-700 group-hover:text-rose-600">{{ t.absent }}</span>
                </button>
              </div>

              <button @click="finishServiceCounter" :disabled="!currentTicket" class="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-black uppercase text-xs tracking-wider shadow-md shadow-emerald-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2.5" d="M5 13l4 4L19 7" /></svg>
                <span>{{ t.endService }}</span>
              </button>
            </div>
          </div>

          <!-- File d'attente latérale du guichet -->
          <div class="space-y-6">
            <div class="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-xs font-black uppercase tracking-widest text-slate-400">{{ t.nextClients }}</h3>
                <span class="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md">{{ waitingTicketsForCounter.length }}</span>
              </div>
              <div class="space-y-2 max-h-[450px] overflow-y-auto pr-1">
                <div v-for="ticket in waitingTicketsForCounter" :key="ticket._id"
                     class="p-3 rounded-xl border border-slate-200/60 bg-slate-50/50 flex justify-between items-center">
                  <div>
                    <p class="text-xs font-black text-slate-900">{{ ticket.queueId?.prefix }}{{ ticket.number }}</p>
                    <p class="text-[9px] font-bold text-slate-400 uppercase">{{ ticket.queueId?.name || t.general }}</p>
                  </div>
                  <span class="text-[10px] font-extrabold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
                    {{ getWaitTime(ticket.createdAt) }} min
                  </span>
                </div>
                <div v-if="waitingTicketsForCounter.length === 0" class="text-center py-10 text-slate-300 font-bold uppercase text-[10px]">
                  {{ t.noTicket }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= MODAL: ASSIGN TICKET TO COUNTER ================= -->
    <div v-if="showAssignModal && selectedTicket" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showAssignModal = false">
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-slate-200">
        <div class="text-center mb-5">
          <h2 class="text-base font-black text-slate-900 uppercase mb-1">{{ t.callTicket }}</h2>
          <p class="text-xs text-blue-600 font-bold">{{ t.ticket }} {{ selectedTicket.queueId?.prefix }}{{ selectedTicket.number }}</p>
        </div>

        <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2.5">{{ t.selectCounterToCall }}</p>
          <div class="space-y-2 max-h-48 overflow-y-auto pr-1">
            <button
              v-for="counter in availableCounters"
              :key="counter._id"
              @click="selectedAssignCounterId = counter._id"
              class="w-full text-left px-3 py-2.5 rounded-lg transition-all border text-xs"
              :class="selectedAssignCounterId === counter._id ? 'bg-blue-50 border-blue-500 font-bold text-blue-700' : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'"
            >
              {{ counter.name }}
            </button>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2.5">
          <button
            @click="showAssignModal = false; selectedTicket = null"
            class="px-4 py-3 bg-slate-100 rounded-xl font-bold uppercase text-xs text-slate-600 hover:bg-slate-200 transition-all"
          >
            {{ t.cancel }}
          </button>
          <button
            @click="callTicketToCounter"
            :disabled="!selectedAssignCounterId"
            class="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold uppercase text-xs hover:bg-blue-700 transition-all shadow-md shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t.call }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import { io } from "socket.io-client";

export default {
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      currentMode: 'admin',
      tickets: [],
      historyTickets: [],
      loading: false,
      socket: null,
      socketConnected: false,
      filterStatus: 'ALL',
      selectedTicket: null,
      showAssignModal: false,
      selectedAssignCounterId: null,
      availableCounters: [],
      selectedCounterId: null,
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          adminPanel: "Console Guichet & POS",
          admin: "Gestionnaire :",
          adminMode: "Vue Globale",
          counterMode: "Poste Caisse",
          waiting: "En attente",
          called: "Appelés",
          completed: "Terminés",
          allTickets: "Gestion des Billets",
          all: "Tous",
          done: "Terminé",
          history: "Historique",
          noHistory: "Aucun historique",
          general: "Général",
          created: "Créé",
          waitTime: "Attente",
          assignToCounter: "Appeler au guichet",
          noTickets: "Aucun ticket",
          recall: "Rappeler",
          selectCounter: "Sélectionner un poste",
          station: "Poste actif",
          changeCounter: "Changer de poste",
          emptyQueue: "File d'attente vide",
          allClientsServed: "Tous les patients ont été servis.",
          inProgress: "En cours de prise en charge",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Appel En Cours",
          next: "Suivant",
          absent: "Absent",
          endService: "Clôturer le ticket",
          nextClients: "Prochains en file",
          noTicket: "Aucun ticket",
          callTicket: "Attribuer le ticket",
          selectCounterToCall: "Choisir la caisse / guichet",
          cancel: "Annuler",
          call: "Lancer l'appel"
        },
        en: {
          adminPanel: "Counter & POS Console",
          admin: "Manager:",
          adminMode: "Overview",
          counterMode: "Counter Station",
          waiting: "Waiting",
          called: "Called",
          completed: "Completed",
          allTickets: "Ticket Management",
          all: "All",
          done: "Done",
          history: "History",
          noHistory: "No history",
          general: "General",
          created: "Created",
          waitTime: "Wait",
          assignToCounter: "Assign to counter",
          noTickets: "No tickets",
          recall: "Recall",
          selectCounter: "Select a station",
          station: "Active Station",
          changeCounter: "Change station",
          emptyQueue: "Empty queue",
          allClientsServed: "All patients have been served.",
          inProgress: "In Progress",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Current Call",
          next: "Next",
          absent: "Absent",
          endService: "Close Ticket",
          nextClients: "Next in line",
          noTicket: "No ticket",
          callTicket: "Assign ticket",
          selectCounterToCall: "Choose counter",
          cancel: "Cancel",
          call: "Call"
        },
        de: {
          adminPanel: "Schalter- & POS-Konsole",
          admin: "Manager:",
          adminMode: "Übersicht",
          counterMode: "Schalterstation",
          waiting: "Wartend",
          called: "Aufgerufen",
          completed: "Abgeschlossen",
          allTickets: "Ticket-Verwaltung",
          all: "Alle",
          done: "Erledigt",
          history: "Verlauf",
          noHistory: "Kein Verlauf",
          general: "Allgemein",
          created: "Erstellt",
          waitTime: "Wartezeit",
          assignToCounter: "Zuweisen",
          noTickets: "Keine Tickets",
          recall: "Erneut rufen",
          selectCounter: "Station wählen",
          station: "Aktive Station",
          changeCounter: "Station wechseln",
          emptyQueue: "Leere Warteschlange",
          allClientsServed: "Alle Patienten wurden bedient.",
          inProgress: "In Bearbeitung",
          service: "Service",
          ticket: "Ticket",
          callNumber: "Aktueller Aufruf",
          next: "Nächster",
          absent: "Abwesend",
          endService: "Ticket schließen",
          nextClients: "Nächste in der Reihe",
          noTicket: "Kein Ticket",
          callTicket: "Ticket zuweisen",
          selectCounterToCall: "Schalter wählen",
          cancel: "Abbrechen",
          call: "Anrufen"
        },
        it: {
          adminPanel: "Console Sportello & POS",
          admin: "Manager:",
          adminMode: "Panoramica",
          counterMode: "Postazione",
          waiting: "In attesa",
          called: "Chiamati",
          completed: "Completati",
          allTickets: "Gestione Biglietti",
          all: "Tutti",
          done: "Fatto",
          history: "Storico",
          noHistory: "Nessuno storico",
          general: "Generale",
          created: "Creato",
          waitTime: "Attesa",
          assignToCounter: "Assegna",
          noTickets: "Nessun ticket",
          recall: "Richiamare",
          selectCounter: "Seleziona postazione",
          station: "Postazione attiva",
          changeCounter: "Cambia postazione",
          emptyQueue: "Coda vuota",
          allClientsServed: "Tutti i pazienti sono stati serviti.",
          inProgress: "In Corso",
          service: "Servizio",
          ticket: "Ticket",
          callNumber: "Chiamata Corrente",
          next: "Successivo",
          absent: "Assente",
          endService: "Chiudi Ticket",
          nextClients: "Prossimi in coda",
          noTicket: "Nessun ticket",
          callTicket: "Assegna ticket",
          selectCounterToCall: "Scegli sportello",
          cancel: "Annulla",
          call: "Chiama"
        }
      }
    };
  },
  watch: {
    currentMode() {
      this.fetchTickets();
    }
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },
    waitingTickets() {
      return this.tickets.filter(t => t.status?.toUpperCase() === 'WAITING');
    },
    calledTickets() {
      return this.tickets.filter(t => t.status?.toUpperCase() === 'CALLED');
    },
    completedTickets() {
      if (!this.historyTickets || !Array.isArray(this.historyTickets)) return [];
      return [...this.historyTickets].sort((a, b) => {
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });
    },
    filteredTickets() {
      if (this.filterStatus === 'ALL') {
        return [...this.waitingTickets, ...this.calledTickets];
      }
      return this.tickets.filter(t => t.status?.toUpperCase() === this.filterStatus);
    },
    waitingTicketsForCounter() {
      if (!this.selectedCounterId) return [];
      return this.tickets.filter(t => t.status?.toUpperCase() === 'WAITING');
    },
    currentTicket() {
      if (!this.selectedCounterId) return null;
      return this.tickets.find(t =>
        t.status?.toUpperCase() === 'CALLED' &&
        t.counterId === this.selectedCounterId
      );
    }
  },
  methods: {
    initSocket() {
      const token = localStorage.getItem("token") || localStorage.getItem("userToken");
      this.socket = io("https://api.datafuse.fr", {
        query: { token: token },
        transports: ['websocket', 'polling'],
        reconnection: true
      });
      this.socket.on("connect", () => { this.socketConnected = true; });
      this.socket.on("queue_updated", () => { this.fetchTickets(); });
      this.socket.on("ticket_called", () => { this.fetchTickets(); });
      this.socket.on("ticket_finished", () => {
        this.fetchTickets();
        this.fetchHistory();
      });
      this.socket.on("disconnect", () => { this.socketConnected = false; });
    },
    async fetchTickets() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");
        let url = "https://api.datafuse.fr/api/agency/admin/all-tickets";
        if (this.currentMode === 'counter' && this.selectedCounterId) {
          url = "https://api.datafuse.fr/api/agency/queues";
        }
        const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok) {
          if (data.tickets) this.tickets = data.tickets;
          else if (data.queue) this.tickets = data.queue;
        }
      } catch (err) {
        console.error("Erreur chargement tickets:", err);
      }
    },
    async fetchHistory() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");
        if (!token) return;
        const res = await fetch("https://api.datafuse.fr/api/agency/history", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.history && Array.isArray(data.history)) {
          this.historyTickets = data.history;
        } else {
          this.historyTickets = [];
        }
      } catch (err) {
        this.historyTickets = [];
      }
    },
    async fetchCounters() {
      try {
        const token = localStorage.getItem("token") || localStorage.getItem("userToken");
        const res = await fetch("https://api.datafuse.fr/api/agency/counters", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (res.ok) {
          this.availableCounters = await res.json();
        }
      } catch (err) {
        console.error("Erreur chargement guichets:", err);
      }
    },
    selectTicketForCall(ticket) {
      this.selectedTicket = ticket;
      this.selectedAssignCounterId = ticket.counterId || null;
      this.showAssignModal = true;
    },
    callTicketToCounter() {
      if (!this.selectedAssignCounterId || !this.selectedTicket) return;
      const counter = this.availableCounters.find(c => c._id === this.selectedAssignCounterId);
      this.socket.emit("call_specific_ticket", {
        ticketId: this.selectedTicket._id,
        counterId: this.selectedAssignCounterId,
        counterName: counter?.name || "Admin",
        agencyId: this.userData.agencyId
      });
      toast.success(`Ticket ${this.selectedTicket.number} appelé au ${counter?.name}`);
      this.showAssignModal = false;
      this.selectedTicket = null;
      this.selectedAssignCounterId = null;
    },
    recallTicketFromHistory(ticket) {
      this.selectedTicket = ticket;
      this.selectedAssignCounterId = ticket.counterId || null;
      this.showAssignModal = true;
    },
    selectCounter(counterId) {
      this.selectedCounterId = counterId;
      this.fetchTickets();
    },
    processNextCounter() {
      if (!this.selectedCounterId) return;
      this.loading = true;
      const counter = this.availableCounters.find(c => c._id === this.selectedCounterId);
      this.socket.emit("call_next", {
        counterId: this.selectedCounterId,
        counterName: counter?.name || "Admin Counter"
      });
      setTimeout(() => { this.loading = false; }, 800);
    },
    callAgainCounter() {
      if (!this.currentTicket) return;
      const counter = this.availableCounters.find(c => c._id === this.selectedCounterId);
      this.socket.emit("recall_ticket", {
        ticketId: this.currentTicket._id,
        counterName: counter?.name || "Admin Counter"
      });
      toast.success("Rappel envoyé");
    },
    markAbsentCounter() {
      if (!this.currentTicket) return;
      this.socket.emit("mark_absent", { ticketId: this.currentTicket._id });
      toast.warning("Client marqué absent");
    },
    finishServiceCounter() {
      if (!this.currentTicket) return;
      this.socket.emit("finish_ticket", {
        ticketId: this.currentTicket._id,
        counterId: this.selectedCounterId,
        agencyId: this.userData.agencyId
      });
      toast.success("Service terminé");
      setTimeout(() => { this.fetchHistory(); }, 500);
    },
    getCounterName(counterId) {
      const counter = this.availableCounters.find(c => c._id === counterId);
      return counter?.name || 'N/A';
    },
    getStatusBadgeClass(status) {
      const s = status?.toUpperCase();
      if (s === 'WAITING') return 'bg-amber-50 text-amber-700 border border-amber-200';
      if (s === 'CALLED') return 'bg-blue-50 text-blue-700 border border-blue-200';
      if (s === 'DONE') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      return 'bg-slate-100 text-slate-700';
    },
    formatTime(dateStr) {
      if (!dateStr) return '--:--';
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    getWaitTime(createdAt) {
      const start = new Date(createdAt);
      const now = new Date();
      return Math.floor((now - start) / 60000);
    }
  },
  mounted() {
    this.fetchTickets();
    this.fetchHistory();
    this.fetchCounters();
    this.initSocket();
    this.historyRefreshInterval = setInterval(() => {
      this.fetchHistory();
    }, 30000);
  },
  beforeUnmount() {
    if (this.socket) this.socket.disconnect();
    if (this.historyRefreshInterval) clearInterval(this.historyRefreshInterval);
  }
};
</script>